import { ID } from './id';

export enum CalendarEventOccurrenceTarget {
  Single = 'single',
  Series = 'series',
}

export enum CalendarEventReminderMethod {
  Popup = 'popup',
  Email = 'email',
}

export enum CalendarEventWarningCode {
  RemindersTruncated = 'reminders-truncated',
  VideoCallUnavailable = 'video-call-unavailable',
}

export enum CalendarEventResponseStatus {
  Accepted = 'accepted',
  Declined = 'declined',
  Tentative = 'tentative',
  NeedsAction = 'needs-action',
}

// Which system minted the video call. Only Meet and Teams are ours to create — a Google event can carry
// a conference some Marketplace add-on attached (Zoom, Webex, ...), and the Calendar API cannot mint one
// of those. So `Other` means "we can show this link and we can destroy it, but we could never put it
// back", which is exactly what has to be said out loud before offering to replace it.
export enum CalendarEventVideoCallProvider {
  GoogleMeet = 'google-meet',
  Teams = 'teams',
  Skype = 'skype',
  Other = 'other',
}

// a calendar is identified by the pair; an event by the triple — a bare
// calendarId is ambiguous across two connections of the same provider
export type CalendarRef = {
  connectionId: ID;
  calendarId: ID;
};

export type CalendarEventRef = CalendarRef & {
  eventId: ID;
};

export type CalendarEventGuest = {
  email: string;
  optional?: boolean;
  // present only on guests read back from the provider; stripped from every write payload
  name?: string | null;
  responseStatus?: CalendarEventResponseStatus;
};

export type CalendarEventReminder = {
  // relative offset before the event start; 0 = at time of event, max 40320 (4 weeks)
  minutes: number;
  method?: CalendarEventReminderMethod;
};

export type CalendarEventWarning = {
  code: CalendarEventWarningCode;
  message: string;
};

export type CalendarEventWriteResult = {
  id: ID;
  warnings: CalendarEventWarning[];
};

// allDay=true: 'YYYY-MM-DD' with end INCLUSIVE; allDay=false: naive 'YYYY-MM-DDTHH:mm:ss'
export type CreateCalendarEventInput = {
  connectionId: ID;
  calendarId: ID;
  title: string;
  description?: string | null;
  location?: string | null;
  allDay?: boolean;
  start: string;
  end: string;
  // IANA zone the naive start/end are expressed in; null/absent = the calendar's own zone
  timeZone?: string | null;
  rrule?: string | null;
  guests?: CalendarEventGuest[] | null;
  videoCall?: boolean;
  busy?: boolean;
  reminders?: CalendarEventReminder[] | null;
};

// partial patch: absent = untouched, null = cleared; allDay/start/end travel together
export type UpdateCalendarEventInput = Partial<
  Omit<CreateCalendarEventInput, 'connectionId' | 'calendarId'>
> &
  CalendarEventRef & {
    // wire field: the backend callable reads `editScope`; renaming it breaks the contract
    editScope: CalendarEventOccurrenceTarget;
    // Overwrite the existing conference with a Meet / Teams one. `videoCall: true` on an event that
    // already has a call means "it still has one" and never replaces it, so this is the only way to swap
    // a third-party link — and the only way to lose one. Set it from a deliberate user action, nothing
    // else. The backend rejects it on a provider that cannot remove a conference (Outlook).
    replaceVideoCall?: boolean;
  };

export type DeleteCalendarEventInput = CalendarEventRef & {
  editScope: CalendarEventOccurrenceTarget;
};

export type RespondToCalendarEventInput = CalendarEventRef & {
  responseStatus: CalendarEventResponseStatus;
  editScope: CalendarEventOccurrenceTarget;
};

export type CalendarEventDetails = {
  id: ID;
  calendarId: ID;
  connectionId: ID;
  title: string;
  description: string | null;
  location: string | null;
  allDay: boolean;
  start: string;
  end: string;
  timeZone: string;
  rrule: string | null;
  guests: CalendarEventGuest[];
  videoCall: boolean;
  videoCallUrl: string | null;
  // absent on responses from a backend deployed before conference-provider support; null when the event
  // has no video call at all. Treat an absent value as "unknown", not as "not Meet" — offering to replace
  // a conference we cannot identify would risk destroying a third-party link.
  videoCallProvider?: CalendarEventVideoCallProvider | null;
  // the provider's own label ("Zoom Meeting"), where it ships one — Graph does not
  videoCallProviderName?: string | null;
  busy: boolean;
  reminders: { minutes: number; method: CalendarEventReminderMethod }[];
  usesDefaultReminders: boolean;
  isRecurring: boolean;
  seriesMasterId: ID | null;
  // the master's rule, display only — posting it back as `rrule` recurs one occurrence
  seriesRrule?: string | null;
  // absent on responses from a backend deployed before RSVP support
  organizer?: { email: string; name: string | null } | null;
  isOrganizer?: boolean;
  selfResponseStatus?: CalendarEventResponseStatus | null;
};
